import { useTranslations } from 'next-intl';
import {
  HeaderSearchBarSuggestion,
  HeaderSearchBarSuggestionList,
  HeaderSearchBarSuggestionsContainer
} from 'components/pcc-components';
import { useCallback } from 'react';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { Image } from 'components/image';
import { useParams } from 'next/navigation';
import { getSeoUrl } from 'utils/links';
import styles from './redesigned-suggestion-list.module.scss';
import cx from 'classnames';
import {
  suggestedCategory,
  suggestedDiagram,
  suggestedKeyword,
  suggestedProducts
} from 'services/searchSuggestions/types';

type RedesignedSuggestionListProps = {
  searchValue: string;
  suggestedKeyWords: Array<suggestedKeyword>;
  suggestedProducts: Array<suggestedProducts>;
  suggestedCategories: Array<suggestedCategory>;
  suggestedDiagrams: Array<suggestedDiagram>;
  isHeadlessCategoryEnabled: boolean;
};

export const RedesignedSuggestionList = ({
  searchValue,
  suggestedKeyWords,
  suggestedProducts,
  suggestedCategories,
  suggestedDiagrams,
  isHeadlessCategoryEnabled
}: RedesignedSuggestionListProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const params = useParams();
  const seoUrl = getSeoUrl(params);

  const keywordLabel = t('SUGGESTED_KEYWORDS');
  const productLabel = t('PRODUCTS');
  const categoryLabel = t('CAT_SHOP_ALL_CATEGORIES');
  const diagramLabel = t('CAT_PARTS_DIAGRAM');

  const trackItemSelection = useCallback(
    (displayName: string) => {
      fireEvent({
        formName: 'search',
        formContent: searchValue,
        formContent2: displayName
      });
    },
    [fireEvent, searchValue]
  );
  return (
    <HeaderSearchBarSuggestionList>
      <HeaderSearchBarSuggestionsContainer>
        <div
          className={cx(
            `${styles['redesigned-suggestion-list-item__keywords']} 
            pe-md-3 pb-3 pb-md-4 
            `
          )}
        >
          <HeaderSearchBarSuggestion
            label={keywordLabel}
            items={suggestedKeyWords}
            onItemClick={trackItemSelection}
            highlightText={searchValue}
            isFederatedSearchEnabled
            listItemClassName={
              styles['redesigned-suggestion-list-item__keywords--padding']
            }
          />
        </div>
        <div
          className={cx(
            `${styles['redesigned-suggestion-list-item__products']} ps-md-3 pb-3 pb-md-4`
          )}
        >
          <HeaderSearchBarSuggestion
            label={productLabel}
            items={suggestedProducts}
            onItemClick={trackItemSelection}
            highlightText={searchValue}
            imgComp={Image}
            isFederatedSearchEnabled
            displayArrow
          />
        </div>
        <div className={cx(`${styles['separator']}`)} />
        <div
          className={cx(
            `${styles['redesigned-suggestion-list-item__diagrams']} 
            ps-md-3 pb-3 pb-md-4 
            `
          )}
        >
          <HeaderSearchBarSuggestion
            label={diagramLabel}
            items={suggestedDiagrams}
            onItemClick={trackItemSelection}
            highlightText={searchValue}
            isFederatedSearchEnabled
            listItemClassName={
              styles['redesigned-suggestion-list-item__diagrams--padding']
            }
            displayArrow
          />
        </div>
        <div
          className={cx(
            `${styles['redesigned-suggestion-list-item__category']} 
            pe-md-3`
          )}
        >
          <HeaderSearchBarSuggestion
            label={categoryLabel}
            items={suggestedCategories}
            onItemClick={trackItemSelection}
            highlightText={searchValue}
            isHeadlessCategoryEnabled={isHeadlessCategoryEnabled}
            seoURL={seoUrl}
            isFederatedSearchEnabled
            listItemClassName={
              styles['redesigned-suggestion-list-item__category--padding']
            }
          />
        </div>
      </HeaderSearchBarSuggestionsContainer>
    </HeaderSearchBarSuggestionList>
  );
};
